import type {
  StorageFile,
  StorageListInput,
  StorageListOrder,
  StorageListPage,
  StorageVisibility,
} from "@gencow/core";
import { STORAGE_LIST_DEFAULT_LIMIT, STORAGE_LIST_MAX_LIMIT, formatBytes } from "@gencow/core";
import type { RawSqlFn, SystemFilesCatalog } from "./storage-types.js";
import { normalizeStorageVisibility } from "./storage-visibility.js";

export type StorageListCursorPayload = {
  v: 1;
  o: StorageListOrder;
  /** Postgres-native timestamp text of the last row returned. */
  t: string;
  /** storage_id of the last row returned — tie-breaker for total order. */
  s: string;
};

export function encodeStorageListCursor(payload: StorageListCursorPayload): string {
  return Buffer.from(JSON.stringify(payload), "utf-8").toString("base64url");
}

export function decodeStorageListCursor(raw: string, order: StorageListOrder): StorageListCursorPayload {
  let parsed: unknown;
  try {
    const json = Buffer.from(raw, "base64url").toString("utf-8");
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Invalid cursor");
  }
  if (
    !parsed ||
    typeof parsed !== "object" ||
    (parsed as StorageListCursorPayload).v !== 1 ||
    typeof (parsed as StorageListCursorPayload).t !== "string" ||
    typeof (parsed as StorageListCursorPayload).s !== "string" ||
    ((parsed as StorageListCursorPayload).o !== "asc" &&
      (parsed as StorageListCursorPayload).o !== "desc")
  ) {
    throw new Error("Invalid cursor");
  }
  const cursor = parsed as StorageListCursorPayload;
  if (cursor.o !== order) throw new Error("Invalid cursor");
  return cursor;
}

export function clampStorageListLimit(requested: number | undefined): number {
  const raw =
    typeof requested === "number" && Number.isFinite(requested)
      ? Math.floor(requested)
      : STORAGE_LIST_DEFAULT_LIMIT;
  if (raw < 1) return 1;
  if (raw > STORAGE_LIST_MAX_LIMIT) return STORAGE_LIST_MAX_LIMIT;
  return raw;
}

export function createSystemFilesCatalog(rawSql: RawSqlFn): SystemFilesCatalog {
  let prepared = false;
  let hasVisibilityColumn = false;

  async function prepare(): Promise<void> {
    if (prepared) return;
    await rawSql(`
        DO $$
        BEGIN
            IF NOT EXISTS (
                SELECT 1 FROM information_schema.tables
                WHERE table_schema = current_schema()
                AND table_name = '_system_files'
            ) THEN
                CREATE TABLE _system_files (
                    id SERIAL PRIMARY KEY,
                    storage_id TEXT NOT NULL,
                    name TEXT NOT NULL,
                    size TEXT NOT NULL,
                    type TEXT NOT NULL DEFAULT 'application/octet-stream',
                    visibility TEXT NOT NULL DEFAULT 'private',
                    uploaded_by TEXT DEFAULT 'api',
                    created_at TIMESTAMP DEFAULT NOW()
                );
            END IF;
        END$$;
    `);
    const visibilityColumns = await rawSql(
      `SELECT 1
         FROM information_schema.columns
        WHERE table_schema = current_schema()
          AND table_name = '_system_files'
          AND column_name = 'visibility'
        LIMIT 1`,
    );
    hasVisibilityColumn = visibilityColumns.length > 0;
    if (!hasVisibilityColumn) {
      try {
        await rawSql(
          `ALTER TABLE _system_files ADD COLUMN IF NOT EXISTS visibility TEXT NOT NULL DEFAULT 'private'`,
        );
        hasVisibilityColumn = true;
      } catch {
        hasVisibilityColumn = false;
      }
    }
    prepared = true;
  }

  return {
    prepare,

    async getUsageBytes(): Promise<number> {
      await prepare();
      const rows = await rawSql(`SELECT COALESCE(SUM(CAST(size AS BIGINT)), 0) AS total FROM _system_files`);
      return Number((rows[0] as Record<string, string>)?.total || "0");
    },

    async assertCanStore(input: { size: number; quotaBytes: number }): Promise<void> {
      if (input.quotaBytes <= 0) return;
      const currentUsage = await this.getUsageBytes();
      const projectedUsage = currentUsage + input.size;
      if (projectedUsage > input.quotaBytes) {
        throw new Error(
          `Storage quota exceeded: ${formatBytes(currentUsage)} used + ${formatBytes(input.size)} new = ${formatBytes(projectedUsage)}, ` +
            `quota is ${formatBytes(input.quotaBytes)}. Delete files or increase quota.`,
        );
      }
    },

    async recordStored(input: StorageFile & { uploadedBy: string }): Promise<void> {
      await prepare();
      if (hasVisibilityColumn) {
        await rawSql(
          `INSERT INTO _system_files (storage_id, name, size, type, visibility, uploaded_by, created_at)
           VALUES ($1, $2, $3, $4, $5, $6, NOW())`,
          [
            input.id,
            input.name,
            String(input.size),
            input.type,
            normalizeStorageVisibility(input.visibility),
            input.uploadedBy,
          ],
        );
      } else {
        await rawSql(
          `INSERT INTO _system_files (storage_id, name, size, type, uploaded_by, created_at)
           VALUES ($1, $2, $3, $4, $5, NOW())`,
          [input.id, input.name, String(input.size), input.type, input.uploadedBy],
        );
      }
    },

    async remove(storageId: string): Promise<void> {
      await rawSql(`DELETE FROM _system_files WHERE storage_id = $1`, [storageId]);
    },

    async getMeta(storageId: string): Promise<StorageFile | null> {
      await prepare();
      const rows = await rawSql(
        hasVisibilityColumn
          ? `SELECT storage_id, name, size, type, visibility FROM _system_files WHERE storage_id = $1 LIMIT 1`
          : `SELECT storage_id, name, size, type FROM _system_files WHERE storage_id = $1 LIMIT 1`,
        [storageId],
      );
      if (rows.length === 0) return null;
      const row = rows[0] as Record<string, string>;
      return {
        id: row.storage_id,
        name: row.name,
        size: Number(row.size),
        type: row.type || "application/octet-stream",
        visibility: normalizeStorageVisibility(row.visibility),
      };
    },

    async listMeta(input?: StorageListInput): Promise<StorageListPage> {
      await prepare();
      const order: StorageListOrder = input?.order === "asc" ? "asc" : "desc";
      const limit = clampStorageListLimit(input?.limit);
      const cursor = input?.cursor ? decodeStorageListCursor(input.cursor, order) : null;

      const direction = order === "asc" ? "ASC" : "DESC";
      const comparator = order === "asc" ? ">" : "<";

      // `created_at` is selected as ::text so the cursor round-trips byte-for-byte
      // without JS Date → ISO → ::timestamp drift (postgres TIMESTAMP WITHOUT TIME
      // ZONE round-trips lossily through JS Date.toISOString in some drivers).
      const params: unknown[] = [];
      let whereClause = "";
      if (cursor) {
        params.push(cursor.t, cursor.s);
        whereClause = `WHERE (created_at, storage_id) ${comparator} ($1::timestamp, $2)`;
      }
      params.push(limit + 1);
      const limitParam = `$${params.length}`;

      const rows = (await rawSql(
        hasVisibilityColumn
          ? `SELECT storage_id, name, size, type, visibility, created_at::text AS created_at_text
           FROM _system_files
           ${whereClause}
           ORDER BY created_at ${direction}, storage_id ${direction}
           LIMIT ${limitParam}`
          : `SELECT storage_id, name, size, type, created_at::text AS created_at_text
           FROM _system_files
           ${whereClause}
           ORDER BY created_at ${direction}, storage_id ${direction}
           LIMIT ${limitParam}`,
        params,
      )) as Array<Record<string, unknown>>;

      const hasMore = rows.length > limit;
      const pageRows = hasMore ? rows.slice(0, limit) : rows;

      const items: StorageFile[] = pageRows.map((row) => ({
        id: String(row.storage_id),
        name: String(row.name),
        size: Number(row.size),
        type: typeof row.type === "string" && row.type ? row.type : "application/octet-stream",
        visibility: normalizeStorageVisibility(row.visibility),
      }));

      let nextCursor: string | null = null;
      if (hasMore && pageRows.length > 0) {
        const last = pageRows[pageRows.length - 1];
        nextCursor = encodeStorageListCursor({
          v: 1,
          o: order,
          t: String(last.created_at_text ?? ""),
          s: String(last.storage_id),
        });
      }

      return { items, nextCursor };
    },

    async setVisibility(storageId: string, visibility: StorageVisibility): Promise<void> {
      await prepare();
      if (!hasVisibilityColumn) return;
      await rawSql(`UPDATE _system_files SET visibility = $1 WHERE storage_id = $2`, [
        normalizeStorageVisibility(visibility),
        storageId,
      ]);
    },
  };
}
